import { useEffect, useState } from "react";
import { X } from "lucide-react";

const STORAGE_KEY = "community_popup_dismissed_v1";

/**
 * Mały, pływający popup w lewym dolnym rogu z informacją "Budujemy to razem!".
 * - Można zamknąć (X) — decyzja zapamiętywana w localStorage.
 * - Pojawia się po krótkiej zwłoce, żeby nie zasłaniać UI od pierwszej klatki.
 * - Na mobile zachowuje bezpieczne marginesy (safe-area).
 */
export const CommunityPopup = () => {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {
      // brak dostępu do localStorage — pokaż mimo wszystko
    }
    const isMobile = window.matchMedia("(max-width: 640px)").matches;
    const delay = isMobile ? 30000 : 15000;

    let timer: number | undefined;
    const schedule = () => {
      timer = window.setTimeout(() => setVisible(true), delay);
    };

    const hasCookieDecision = () => {
      try {
        return !!localStorage.getItem("gdzieschronisko_cookie_consent");
      } catch {
        return true;
      }
    };

    if (hasCookieDecision()) {
      schedule();
    } else {
      const onChange = () => {
        window.removeEventListener("cookie-consent-change", onChange);
        schedule();
      };
      window.addEventListener("cookie-consent-change", onChange);
      return () => {
        window.removeEventListener("cookie-consent-change", onChange);
        if (timer) window.clearTimeout(timer);
      };
    }

    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    setClosing(true);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignoruj błąd
    }
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 250);
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed z-[1200] left-3 sm:left-4 bottom-3 sm:bottom-4 w-[260px] sm:w-auto sm:max-w-sm ${closing ? "animate-fade-out" : "animate-in fade-in slide-in-from-bottom-4 duration-500"}`}
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      role="status"
      aria-live="polite"
    >
      <div className="relative flex items-start gap-2 p-3 pr-9 bg-accent/95 backdrop-blur border border-primary/20 rounded-xl shadow-lg">
        <span className="text-base leading-none mt-0.5" aria-hidden="true">
          🐾
        </span>
        <p className="text-[11px] sm:text-xs text-accent-foreground leading-snug">
          <strong>Budujemy to razem!</strong> Każdy może aktualizować potrzeby
          schronisk. Masz świeże info? Kliknij na schronisko i zgłoś
          aktualizację.
        </p>
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-1.5 right-1.5 p-1 rounded-md text-muted-foreground hover:bg-muted/60 transition-colors"
          aria-label="Zamknij"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
